<template>
  <div class="raffle-campaign-participants-report">
    <div class="category-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" href="#" @click.prevent="backButton">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </p>
      </div>
    </div>

    <div class="sales-report mar-to-15">
      <div class="col-md-12 mar-bt-15">
        <p class="text-muted">
          <span>{{ $t('reports.raffleCampaignsReport') }}</span>
          {{ $t('general.breadcrumbSeparator') }}
          <span>{{ campaignTitle }}</span>
          <template v-if="level === 'orders'">
            {{ $t('general.breadcrumbSeparator') }}{{ participantTitle }}
          </template>
        </p>
      </div>

      <div v-if="level === 'participants'" class="col-md-12">
        <div class="card">
          <div class="card-content">
            <div class="raffle-campaign-participants-report__filters">
              <div class="form-group" style="flex: 0 0 110px; margin: 0;">
                <label>{{ $t('raffleCampaigns.userId') }}</label>
                <el-input
                  v-model="participantFilters.user_id"
                  :placeholder="$t('raffleCampaigns.userId')"
                  size="small"
                  clearable
                  @keyup.enter.native="applyParticipantFilters"
                />
              </div>
              <div class="form-group" style="flex: 1 1 200px; margin: 0;">
                <label>{{ $t('raffleCampaigns.search') }}</label>
                <el-input
                  v-model="participantFilters.search"
                  :placeholder="$t('raffleCampaigns.searchParticipants')"
                  size="small"
                  clearable
                  @keyup.enter.native="applyParticipantFilters"
                />
              </div>
              <div class="form-group" style="flex: 0 0 240px; margin: 0;">
                <label>{{ $t('raffleCampaigns.joinedAt') }}</label>
                <el-date-picker
                  type="daterange"
                  v-model="participantJoinedRange"
                  format="yyyy-MM-dd"
                  value-format="yyyy-MM-dd"
                  size="small"
                  :placeholder="$t('raffleCampaigns.joinedAt')"
                  style="width: 100%;"
                />
              </div>
              <div class="form-group" style="flex: 0 0 100px; margin: 0;">
                <button class="btn add" @click="applyParticipantFilters" style="width: 100%; font-size: 12px; padding: 4px 8px;">
                  <i class="ti-search"></i> {{ $t('general.search') }}
                </button>
              </div>
            </div>

            <data-tables-server
              class="campaign-table width-table raffle-campaign-participants-report__table"
              @query-change="onParticipantsPage"
              v-loading="loading"
              :data="participantsRows"
              :total="participantsTotal"
              :pagination-props="paginationProps"
              :table-props="{ border: false }"
              style="width: 100%"
              @row-click="onParticipantRow"
            >
              <el-table-column :min-width="90" :label="$t('raffleCampaigns.userId')">
                <template slot-scope="p">
                  {{ p.row.user_id || p.row.id || '-' }}
                </template>
              </el-table-column>
              <el-table-column :min-width="160" :label="$t('reports.email')">
                <template slot-scope="p">
                  <span class="raffle-report-table__ellipsis">{{ p.row.email || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column :min-width="150" :label="$t('reports.name')">
                <template slot-scope="p">
                  <span class="raffle-report-table__ellipsis">{{ displayPersonName(p.row) }}</span>
                </template>
              </el-table-column>
              <el-table-column :min-width="140" :label="$t('raffleCampaigns.joinedAt')">
                <template slot-scope="p">
                  {{ formatDate(p.row.joined_at) }}
                </template>
              </el-table-column>
              <el-table-column :min-width="110" :label="$t('raffleCampaigns.score')">
                <template slot-scope="p">
                  <b>{{ formatScore(p.row) }}</b>
                </template>
              </el-table-column>
              <el-table-column :min-width="110" :label="$t('raffleCampaigns.ordersCount')">
                <template slot-scope="p">
                  {{ p.row.orders_count != null ? p.row.orders_count : 0 }}
                </template>
              </el-table-column>
              <el-table-column :min-width="120" :label="$t('raffleCampaigns.ordersTotal')">
                <template slot-scope="p">
                  {{ formatMoney(p.row.orders_total) }} {{ currency }}
                </template>
              </el-table-column>
            </data-tables-server>
          </div>
        </div>
      </div>

      <div v-if="level === 'orders'" class="col-md-12">
        <div class="card">
          <div class="card-content">
            <div class="raffle-campaign-participants-report__filters">
              <div class="form-group" style="flex: 0 0 120px; margin: 0;">
                <label>{{ $t('general.orderId') }}</label>
                <el-input
                  v-model="orderFilters.order_id"
                  :placeholder="$t('general.orderId')"
                  size="small"
                  clearable
                  @keyup.enter.native="applyOrderFilters"
                />
              </div>
              <div class="form-group" style="flex: 1 1 160px; margin: 0;">
                <label>{{ $t('raffleCampaigns.orderNumber') }}</label>
                <el-input
                  v-model="orderFilters.order_number"
                  :placeholder="$t('raffleCampaigns.orderNumber')"
                  size="small"
                  clearable
                  @keyup.enter.native="applyOrderFilters"
                />
              </div>
              <div class="form-group" style="flex: 0 0 160px; margin: 0;">
                <label>{{ $t('orders.status') }}</label>
                <el-select
                  v-model="orderFilters.status"
                  clearable
                  size="small"
                  :placeholder="$t('general.select')"
                  style="width: 100%;"
                >
                  <el-option
                    v-for="status in orderStatusOptions"
                    :key="status"
                    :label="$t('ordersStatus.' + status)"
                    :value="status"
                  />
                </el-select>
              </div>
              <div class="form-group" style="flex: 0 0 240px; margin: 0;">
                <label>{{ $t('raffleCampaigns.registeredAt') }}</label>
                <el-date-picker
                  type="daterange"
                  v-model="orderDateRange"
                  format="yyyy-MM-dd"
                  value-format="yyyy-MM-dd"
                  size="small"
                  :placeholder="$t('raffleCampaigns.registeredAt')"
                  style="width: 100%;"
                />
              </div>
              <div class="form-group" style="flex: 0 0 100px; margin: 0;">
                <button class="btn add" @click="applyOrderFilters" style="width: 100%; font-size: 12px; padding: 4px 8px;">
                  <i class="ti-search"></i> {{ $t('general.search') }}
                </button>
              </div>
            </div>

            <data-tables-server
              class="campaign-table width-table raffle-campaign-participants-report__table"
              @query-change="onOrdersPage"
              v-loading="loading"
              :data="ordersRows"
              :total="ordersTotal"
              :pagination-props="paginationProps"
              :table-props="{ border: false }"
              style="width: 100%"
            >
              <el-table-column :min-width="100" :label="$t('general.orderId')">
                <template slot-scope="p">
                  {{ p.row.order_id || p.row.id || '-' }}
                </template>
              </el-table-column>
              <el-table-column :min-width="130" :label="$t('raffleCampaigns.orderNumber')">
                <template slot-scope="p">
                  {{ p.row.order_number || '-' }}
                </template>
              </el-table-column>
              <el-table-column :min-width="120" :label="$t('orders.status')">
                <template slot-scope="p">
                  {{ orderStatusLabel(p.row.order_status) }}
                </template>
              </el-table-column>
              <el-table-column :min-width="120" :label="$t('orders.totals_grandtotal')">
                <template slot-scope="p">
                  {{ formatMoney(p.row.order_total) }} {{ currency }}
                </template>
              </el-table-column>
              <el-table-column :min-width="140" :label="$t('raffleCampaigns.orderCreatedAt')">
                <template slot-scope="p">
                  {{ formatDate(p.row.order_created_at) }}
                </template>
              </el-table-column>
              <el-table-column :min-width="140" :label="$t('raffleCampaigns.registeredAt')">
                <template slot-scope="p">
                  {{ formatDate(p.row.registered_at) }}
                </template>
              </el-table-column>
            </data-tables-server>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { Input, Select, Option, DatePicker } from 'element-ui'
import DataTables from 'vue-data-tables'
import swal from 'sweetalert2'

Vue.use(DataTables)
Vue.use(Input)
Vue.use(Select)
Vue.use(Option)
Vue.use(DatePicker)

export default {
  props: {
    route: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loading: false,
      level: 'participants',
      paginationProps: { layout: 'prev, pager, next, sizes, total' },
      participantsRows: [],
      participantsTotal: 0,
      ordersRows: [],
      ordersTotal: 0,
      selectedCampaignId: null,
      selectedUserId: null,
      participantTitle: '',
      skipParticipantsQueryChange: true,
      skipOrdersQueryChange: false,
      participantFilters: {
        user_id: '',
        search: ''
      },
      participantJoinedRange: [],
      orderFilters: {
        order_id: '',
        order_number: '',
        status: null
      },
      orderDateRange: [],
      orderStatusOptions: [
        'NEW',
        'PROCESSING',
        'PREPARED',
        'TRANSPORT',
        'DELIVERED',
        'CANCELED',
        'ON_HOLD',
        'POSTPONED',
        'COMPLETED'
      ],
      currency: this.getCurrencySymbol(this.$session.get('selectedCompanyData')['currency_iso'])
    }
  },
  computed: {
    campaignTitle () {
      const q = this.route.query || {}
      if (q.title) return q.title
      const id = this.selectedCampaignId
      return id != null ? this.$t('general.idHash', { id }) : ''
    }
  },
  created () {
    const raw = this.route.params && this.route.params.campaignId
    this.selectedCampaignId = raw != null && raw !== '' ? raw : null
    if (this.selectedCampaignId == null) {
      swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
      return
    }
    this.fetchParticipants(1, 25)
  },
  methods: {
    backButton () {
      if (this.level === 'orders') {
        this.level = 'participants'
        this.selectedUserId = null
        this.orderFilters = { order_id: '', order_number: '', status: null }
        this.orderDateRange = []
        this.skipParticipantsQueryChange = true
        this.fetchParticipants(1, 25)
        return
      }
      this.$bus.$emit('close-modal')
    },
    formatMoney (value) {
      const n = parseFloat(value || 0)
      return isNaN(n) ? '0.00' : n.toFixed(2)
    },
    formatScore (row) {
      if (row.score == null) return 0
      if (row.type === 'order_total') {
        return this.formatMoney(row.score)
      }
      return row.score
    },
    orderStatusLabel (status) {
      if (!status) return '-'
      const key = 'ordersStatus.' + status
      const translated = this.$t(key)
      return translated === key ? status : translated
    },
    displayPersonName (row) {
      if (!row) return this.$t('general.emDash')
      const fn = String(row.firstname || row.first_name || '').trim()
      const ln = String(row.lastname || row.last_name || '').trim()
      const s = [fn, ln].filter(Boolean).join(' ')
      if (s) return s
      if (row.email) return row.email
      return this.$t('general.idHash', { id: row.user_id || row.id })
    },
    userIdFromRow (row) {
      return row.user_id || row.id
    },
    onParticipantRow (row) {
      this.selectedUserId = this.userIdFromRow(row)
      this.participantTitle = this.displayPersonName(row)
      this.level = 'orders'
      this.orderFilters = { order_id: '', order_number: '', status: null }
      this.orderDateRange = []
      this.skipOrdersQueryChange = true
      this.fetchOrders(1, 25)
    },
    normalizeRows (response) {
      if (!response || !response.data) return []
      const d = response.data
      if (Array.isArray(d)) return d
      if (Array.isArray(d.data)) return d.data
      if (Array.isArray(d.participants)) return d.participants
      if (Array.isArray(d.orders)) return d.orders
      return []
    },
    normalizeTotal (response) {
      if (response.pagination && response.pagination.total != null) return response.pagination.total
      if (response.meta && response.meta.total != null) return response.meta.total
      const d = response.data
      if (d && d.pagination && d.pagination.total != null) return d.pagination.total
      if (d && d.total != null) return d.total
      return this.normalizeRows(response).length
    },
    baseParams () {
      return {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
    },
    applyParticipantFilters () {
      this.skipParticipantsQueryChange = true
      this.fetchParticipants(1, 25)
    },
    applyOrderFilters () {
      this.skipOrdersQueryChange = true
      this.fetchOrders(1, 25)
    },
    buildParticipantParams (page, perPage) {
      const data = Object.assign({}, this.baseParams(), { page, per_page: perPage, perPage })
      const userId = String(this.participantFilters.user_id || '').trim()
      if (userId !== '') {
        data.user_id = userId
      }
      const search = String(this.participantFilters.search || '').trim()
      if (search !== '') {
        data.search = search
      }
      if (this.participantJoinedRange && this.participantJoinedRange.length === 2 && this.participantJoinedRange[0] && this.participantJoinedRange[1]) {
        data.joined_from = this.participantJoinedRange[0]
        data.joined_to = this.participantJoinedRange[1]
      }
      return data
    },
    buildOrderParams (page, perPage) {
      const data = Object.assign({}, this.baseParams(), { page, per_page: perPage, perPage })
      const orderId = String(this.orderFilters.order_id || '').trim()
      if (orderId !== '') {
        data.order_id = orderId
      }
      const orderNumber = String(this.orderFilters.order_number || '').trim()
      if (orderNumber !== '') {
        data.order_number = orderNumber
      }
      if (this.orderFilters.status) {
        data.status = this.orderFilters.status
      }
      if (this.orderDateRange && this.orderDateRange.length === 2 && this.orderDateRange[0] && this.orderDateRange[1]) {
        data.date_from = this.orderDateRange[0]
        data.date_to = this.orderDateRange[1]
      }
      return data
    },
    onParticipantsPage (q) {
      if (this.skipParticipantsQueryChange) {
        this.skipParticipantsQueryChange = false
        return
      }
      this.fetchParticipants(q.page, q.pageSize)
    },
    fetchParticipants (page, perPage) {
      if (!this.selectedCampaignId) return
      this.loading = true
      const data = this.buildParticipantParams(page, perPage)
      const path = '/reports/raffle-campaigns/' + this.selectedCampaignId + '/participants'
      this.apiRequest(path, 'GET', data, (response) => {
        if (response.status) {
          this.participantsRows = this.normalizeRows(response)
          this.participantsTotal = this.normalizeTotal(response)
        } else {
          swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
        }
        this.loading = false
      })
    },
    onOrdersPage (q) {
      if (this.skipOrdersQueryChange) {
        this.skipOrdersQueryChange = false
        return
      }
      this.fetchOrders(q.page, q.pageSize)
    },
    fetchOrders (page, perPage) {
      if (!this.selectedCampaignId || !this.selectedUserId) return
      this.loading = true
      const data = this.buildOrderParams(page, perPage)
      const path = '/reports/raffle-campaigns/' + this.selectedCampaignId + '/participants/' + this.selectedUserId + '/orders'
      this.apiRequest(path, 'GET', data, (response) => {
        if (response.status) {
          this.ordersRows = this.normalizeRows(response)
          this.ordersTotal = this.normalizeTotal(response)
        } else if (response.errors && response.errors.user_id) {
          swal('', response.errors.user_id, 'error').catch(() => {})
          this.level = 'participants'
        } else {
          swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
        }
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.raffle-campaign-participants-report__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
  margin-bottom: 15px;
}

.raffle-campaign-participants-report >>> div.campaign-table div.pagination-bar {
  display: block !important;
}

.raffle-campaign-participants-report >>> .el-table .cell {
  overflow: hidden;
}

.raffle-campaign-participants-report >>> .el-table .cell > .raffle-report-table__ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Reports/RaffleCampaignParticipantsReport.vue